import { ListingEntry } from 'graphql/listings/queries';
import { createOrder } from 'graphql/orders/mutations';
import { useGetListings } from 'hooks/useGetListings';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import useAlert from 'utils/alert';
import {
  CLOSING_DISCLOSURE,
  CURRENT_PATH,
  DEFAULT_ROUTE,
  PURCHASE,
  PURCHASE_STANDARD,
  REFINANCE,
} from 'utils/constants';
import { capitalize } from 'utils/helpers';
import { useUserInformationContext } from 'contexts/userContext';
import { usePreferences } from 'contexts/preferences-context';

export type Fields = {
  transactionType: string;
  type: string;
  workflow: string;
  agencyId: string;
};

const useModalCreateOrdersHook = (onCancel: () => void, open: boolean) => {
  const history = useHistory();
  const { t } = useTranslation();
  const showAlert = useAlert();
  const { currentUser } = useUserInformationContext();
  const { selectedAgencyId, setSelectedAgencyId } = usePreferences() as any;

  const queryClient = useQueryClient();

  const methods = useForm<Fields>({
    defaultValues: {
      transactionType: PURCHASE,
      type: CLOSING_DISCLOSURE,
      workflow: PURCHASE_STANDARD,
      agencyId: selectedAgencyId || currentUser?.accesses[0]?._id || '',
    },
  });

  const [workflows, setWorkflows] = useState<ListingEntry[]>([
    {
      code: 'Purchase Standard',
      description: 'Purchase Standard',
    },
  ]);

  const { data: transactionsResponse } = useGetListings({
    code: 'allowed_transactions',
  });
  const transactionTypes = transactionsResponse?.getListing.entries ?? [];

  const { data: typesResponse } = useGetListings({
    code: 'allowed_types',
  });
  const types = typesResponse?.getListing.entries ?? [];

  const { mutate: mutationCreateOrder, ...responseCreateOrder } = useMutation(createOrder);

  useEffect(() => {
    if (responseCreateOrder.status === 'success' && responseCreateOrder.data) {
      onCancel();
      responseCreateOrder.reset();
      queryClient.invalidateQueries('orders');
      localStorage.setItem(CURRENT_PATH, JSON.stringify({ view: DEFAULT_ROUTE, param: '' }));
      history.push(`/orders/details/${responseCreateOrder.data.createOrder._id}/${DEFAULT_ROUTE}`);
      showAlert(capitalize(t('dialogs:order-created')), 'success');
    }

    if (responseCreateOrder.isError) {
      onCancel();
      responseCreateOrder.reset();
    }
  }, [responseCreateOrder, onCancel, history, t, showAlert, queryClient]);

  useEffect(() => {
    if (open) {
      methods.reset({
        transactionType: PURCHASE,
        type: CLOSING_DISCLOSURE,
        workflow: PURCHASE_STANDARD,
        agencyId: selectedAgencyId || currentUser?.accesses[0]?._id || '',
      });
      setWorkflows([
        {
          code: 'Purchase Standard',
          description: 'Purchase Standard',
        },
      ]);
    }
  }, [open, selectedAgencyId, currentUser, methods]);

  const selectTransactionType = (code: string) => {
    const workflowList: ListingEntry[] = [];
    if (code === PURCHASE) {
      workflowList.push({
        code: 'Purchase Standard',
        description: 'Purchase Standard',
      });
    }

    if (code === REFINANCE) {
      workflowList.push({
        code: 'Refinance',
        description: 'Refinance',
      });
    }

    methods.setValue('type', '');
    methods.setValue('workflow', '');
    setWorkflows(workflowList);
  };

  const handleOnSubmit = async (values: Fields) => {
    setSelectedAgencyId(values.agencyId);
    await mutationCreateOrder(values);
    methods.reset();
  };

  return {
    t,
    methods,
    transactionTypes,
    types,
    workflows,
    selectTransactionType,
    handleOnSubmit,
  };
};

export default useModalCreateOrdersHook;
